import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../app/providers/AuthProvider';
import { UserSidebarLayout } from '../components/UserSidebarLayout';
import { COLORS } from '../../../core/constants/colors';
import { Company } from '../types';
import { api } from '../../../core/utils/apiClient';

export const QueryPage: React.FC = () => {
  const { authState } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [queryText, setQueryText] = useState('');
  const [selectedCompany, setSelectedCompany] = useState<number | null>(null);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [isLoading] = useState(false);

  useEffect(() => {
    const companyParam = searchParams.get('company');
    if (companyParam) {
      setSelectedCompany(parseInt(companyParam));
    }
    loadCompanies();
  }, [searchParams]);

  const loadCompanies = async () => {
    try {
      const companiesResponse = await api.get('/api/v1/companies/');
      if (companiesResponse.ok) {
        const companiesData = await companiesResponse.json();
        setCompanies(companiesData.companies || []);
      }
    } catch (error) {
      console.error('Error loading companies:', error);
    }
  };

  const handleSubmitQuery = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!queryText.trim()) return;

    // IMMEDIATELY redirect to chat page with the query
    // The ChatPage will handle the API call and show thinking indicator
    navigate('/dashboard/chat', {
      state: {
        pendingQuery: queryText,
        companyId: selectedCompany,
        isNewQuery: true
      },
      replace: false
    });
  };

  const suggestedPrompts = [
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      title: 'Analyze Portfolio',
      description: 'Get insights on company performance and metrics'
    },
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>
      ),
      title: 'Generate Report',
      description: 'Create comprehensive reports from your data'
    },
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
      title: 'Compare Companies',
      description: 'Compare metrics between portfolio companies'
    }
  ];

  return (
    <UserSidebarLayout>
      <div className="flex items-center justify-center min-h-screen bg-white px-6" style={{ fontFamily: 'DM Sans, sans-serif' }}>
        <div className="w-full max-w-3xl mx-auto text-center">
          {/* Welcome bubble */}
          <div className="mb-8 flex justify-center">
            <div className="relative w-32 h-32">
              <div 
                className="absolute inset-0 rounded-full blur-2xl opacity-60"
                style={{ 
                  background: `radial-gradient(circle, ${COLORS.user.primary}40 0%, ${COLORS.user.primaryLight}50 50%, transparent 70%)`
                }}
              />
              <div 
                className="absolute inset-4 rounded-full"
                style={{ 
                  background: `radial-gradient(circle at 30% 30%, ${COLORS.user.primaryLight} 0%, ${COLORS.user.primary}CC 50%, ${COLORS.user.primary}AA 100%)`,
                  boxShadow: `0 8px 32px ${COLORS.user.primary}4D, inset 0 -8px 16px rgba(255, 255, 255, 0.3), inset 0 8px 16px ${COLORS.user.primary}33`
                }}
              />
            </div>
          </div>

          {/* Greeting */}
          <h1 className="text-4xl font-normal mb-2" style={{ color: COLORS.user.primary }}>
            Hello, {authState.user?.full_name?.split(' ')[0] || 'there'}
          </h1>
          <h2 className="text-4xl font-semibold mb-12 text-gray-900">
            How can I assist you today?
          </h2>

          {/* Search Input with Company Dropdown */}
          <form onSubmit={handleSubmitQuery} className="mb-8">
            <div className="relative mb-4">
              <input
                type="text"
                value={queryText}
                onChange={(e) => setQueryText(e.target.value)}
                placeholder="Ask me anything..."
                disabled={isLoading}
                className="w-full px-6 py-4 text-base rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: '#FAFAFA' }}
              />
              {/* Company Filter inside input */}
              {companies.length > 0 && (
                <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                  <select
                    value={selectedCompany || ''}
                    onChange={(e) => setSelectedCompany(e.target.value ? parseInt(e.target.value) : null)}
                    disabled={isLoading}
                    className="px-3 py-1.5 text-xs rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-200 disabled:opacity-50 bg-white"
                    style={{ color: COLORS.user.primary }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <option value="">All Companies</option>
                    {companies.map((company) => (
                      <option key={company.id} value={company.id}>
                        {company.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </form>

          {/* Suggested Prompts Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {suggestedPrompts.map((prompt, index) => (
              <button
                key={index}
                onClick={() => setQueryText(prompt.title)}
                className="p-6 rounded-xl border border-gray-200 hover:border-purple-200 hover:shadow-sm transition-all text-left bg-white"
              >
                <div className="text-gray-600 mb-3">{prompt.icon}</div>
                <h3 className="font-semibold text-gray-900 mb-2">{prompt.title}</h3>
                <p className="text-sm text-gray-500">{prompt.description}</p>
              </button>
            ))}
          </div>
        </div>
      </div>
    </UserSidebarLayout>
  );
};
